import { supabase } from './supabase';
import type { SubscriptionPlan } from './subscription';

export interface HospitalWithSubscription {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  subscription_status: 'trial' | 'active' | 'expired' | 'suspended' | 'cancelled';
  subscription_expires_at: string | null;
  trial_started_at: string | null;
  trial_days: number;
  subscription_plan_id: string | null;
  payment_status: 'pending' | 'paid' | 'failed' | 'refunded';
  last_payment_date: string | null;
  next_payment_date: string | null;
  created_at: string;
}

export interface AllPaymentHistory {
  id: string;
  hospital_id: string;
  hospital_name: string;
  subscription_plan_id: string;
  plan_name: string;
  amount: number;
  currency: string;
  payment_method: string;
  transaction_id: string | null;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  subscription_period_months: number;
  payment_date: string;
  notes: string | null;
  created_at: string;
}

/**
 * Get all hospitals (super admin only)
 */
export async function getAllHospitals(): Promise<HospitalWithSubscription[]> {
  try {
    const { data, error } = await supabase
      .from('hospitals')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching hospitals:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error getting all hospitals:', error);
    return [];
  }
}

/**
 * Get all payment history (super admin only)
 */
export async function getAllPaymentHistory(): Promise<AllPaymentHistory[]> {
  try {
    const { data: payments, error: paymentsError } = await supabase
      .from('payment_history')
      .select('*')
      .order('payment_date', { ascending: false });

    if (paymentsError) {
      console.error('Error fetching payments:', paymentsError);
      return [];
    }

    // Get hospital names and plan names
    const hospitalIds = [...new Set(payments?.map(p => p.hospital_id) || [])];
    const planIds = [...new Set(payments?.map(p => p.subscription_plan_id).filter(Boolean) || [])];

    const { data: hospitals } = await supabase
      .from('hospitals')
      .select('id, name')
      .in('id', hospitalIds);

    const { data: plans } = await supabase
      .from('subscription_plans')
      .select('id, name')
      .in('id', planIds);

    const hospitalMap = new Map(hospitals?.map(h => [h.id, h.name]) || []);
    const planMap = new Map(plans?.map(p => [p.id, p.name]) || []);

    return (payments || []).map(payment => ({
      ...payment,
      hospital_name: hospitalMap.get(payment.hospital_id) || 'Unknown',
      plan_name: payment.subscription_plan_id ? (planMap.get(payment.subscription_plan_id) || 'Unknown') : 'N/A'
    }));
  } catch (error) {
    console.error('Error getting all payment history:', error);
    return [];
  }
}

/**
 * Activate subscription for a hospital (super admin only)
 */
export async function activateHospitalSubscription(
  hospitalId: string,
  planId: string,
  paymentMethod: string = 'manual'
): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('activate_subscription_demo', {
      p_hospital_id: hospitalId,
      p_plan_id: planId,
      p_payment_method: paymentMethod
    });

    if (error) {
      console.error('Error activating subscription:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in activate subscription:', error);
    return false;
  }
}

/**
 * Get subscription statistics
 */
export async function getSubscriptionStats() {
  try {
    const hospitals = await getAllHospitals();
    
    const stats = {
      total: hospitals.length,
      trial: hospitals.filter(h => h.subscription_status === 'trial').length,
      active: hospitals.filter(h => h.subscription_status === 'active').length,
      expired: hospitals.filter(h => h.subscription_status === 'expired').length,
      suspended: hospitals.filter(h => h.subscription_status === 'suspended').length,
      cancelled: hospitals.filter(h => h.subscription_status === 'cancelled').length
    };

    const payments = await getAllPaymentHistory();
    const completedPayments = payments.filter(p => p.status === 'completed');
    const totalRevenue = completedPayments.reduce((sum, p) => sum + Number(p.amount), 0);

    return {
      ...stats,
      totalRevenue,
      totalPayments: completedPayments.length
    };
  } catch (error) {
    console.error('Error getting subscription stats:', error);
    return {
      total: 0,
      trial: 0,
      active: 0,
      expired: 0,
      suspended: 0,
      cancelled: 0,
      totalRevenue: 0,
      totalPayments: 0
    };
  }
}

/**
 * Create a new subscription plan (super admin only)
 */
export async function createSubscriptionPlan(
  plan: Omit<SubscriptionPlan, 'id'>
): Promise<SubscriptionPlan | null> {
  try {
    const { data, error } = await supabase
      .from('subscription_plans')
      .insert({
        name: plan.name,
        duration_months: plan.duration_months,
        price: plan.price,
        currency: plan.currency || 'INR',
        features: plan.features || {},
        is_active: plan.is_active !== undefined ? plan.is_active : true
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating subscription plan:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in create subscription plan:', error);
    return null;
  }
}

/**
 * Update a subscription plan (super admin only)
 */
export async function updateSubscriptionPlan(
  planId: string,
  updates: Partial<Omit<SubscriptionPlan, 'id'>>
): Promise<SubscriptionPlan | null> {
  try {
    const { data, error } = await supabase
      .from('subscription_plans')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', planId)
      .select()
      .single();

    if (error) {
      console.error('Error updating subscription plan:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in update subscription plan:', error);
    return null;
  }
}

/**
 * Delete a subscription plan (super admin only)
 * Note: Only deletes if no hospitals are using it
 */
export async function deleteSubscriptionPlan(planId: string): Promise<boolean> {
  try {
    // Check if any hospitals are using this plan
    const { data: hospitals, error: checkError } = await supabase
      .from('hospitals')
      .select('id')
      .eq('subscription_plan_id', planId)
      .limit(1);

    if (checkError) {
      console.error('Error checking plan usage:', checkError);
      return false;
    }

    if (hospitals && hospitals.length > 0) {
      throw new Error('Cannot delete plan: Some hospitals are using this plan');
    }

    const { error } = await supabase
      .from('subscription_plans')
      .delete()
      .eq('id', planId);

    if (error) {
      console.error('Error deleting subscription plan:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in delete subscription plan:', error);
    throw error;
  }
}

/**
 * Get all subscription plans (super admin only - includes inactive)
 */
export async function getAllSubscriptionPlans(): Promise<SubscriptionPlan[]> {
  try {
    const { data, error } = await supabase
      .from('subscription_plans')
      .select('*')
      .order('duration_months', { ascending: true });

    if (error) {
      console.error('Error fetching subscription plans:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error getting all subscription plans:', error);
    return [];
  }
}

